/** @format */

import {useState, MouseEvent} from 'react';

import {useLocation, useNavigate} from 'react-router-dom';

import {School, SupportAgent, Videocam} from '@mui/icons-material';

import {Trans, useTranslation} from 'react-i18next';

import {useTheme} from '@mui/material/styles';

import {ROUTES} from 'constants/ROUTES';
import {useMixpanelContext} from 'contexts/MixpanelContext';
import {useCompanyContext} from 'contexts/CompanyContext';
import {IsRuVersion} from 'helpers/routerHelpers';

import {BookDemoModal} from 'components/BookDemoModal';

import {ButtonHelp, HelpContainer, StyledMenu, StyledMenuItem} from './styles';
import {getAnchor} from '../../helpers/getAnchor';
import QuestionMarkIcon from './question-mark-icon.svg?react';
import {TELEGRAM_SUPPORT_URL} from './constants';

const HelpFab = () => {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {mixpanel} = useMixpanelContext();
  const {currentCompany} = useCompanyContext();
  const {t} = useTranslation();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isBookDemoOpen, setIsBookDemoOpen] = useState(false);
  const open = Boolean(anchorEl);

  // не показываем кнопку на страницах авторизации/регистрации и для не-RU версии
  const shouldHide = pathname.startsWith('/auth') || !IsRuVersion();

  if (shouldHide) {
    return null;
  }

  // не показываем меню материалов на странице с материалами
  const isHideMaterials = pathname.startsWith('/help/materials');

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    mixpanel.track('click_help_header', {
      project_uuid: currentCompany?.uuid,
      source: pathname,
    });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClickTelegram = () => {
    mixpanel.track('click_help_telegram', {
      project_uuid: currentCompany?.uuid,
    });
    window.open(TELEGRAM_SUPPORT_URL, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const onClickMaterials = () => {
    mixpanel.track('click_help_materials', {
      project_uuid: currentCompany?.uuid,
    });
    handleClose();
    const anchor = getAnchor(pathname);
    navigate(`/${ROUTES.HELP_MATERIALS}${anchor}`);
  };

  const onClickBookDemo = () => {
    mixpanel.track('click_book_demo_button', {
      project_uuid: currentCompany?.uuid,
    });
    handleClose();
    setIsBookDemoOpen(true);
  };

  const handleCloseBookDemo = () => {
    setIsBookDemoOpen(false);
  };

  return (
    <HelpContainer>
      <ButtonHelp onClick={handleOpen} active={open} aria-label={t('Помощь')}>
        <QuestionMarkIcon />
      </ButtonHelp>

      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              background: 'transparent',
              transform: 'translateY(-10px) !important',
              marginTop: theme.spacing(4),
            },
          },
        }}
      >
        <StyledMenuItem onClick={onClickBookDemo}>
          <Videocam />
          <Trans>Онлайн-демо со специалистом</Trans>
        </StyledMenuItem>
        <StyledMenuItem onClick={onClickTelegram}>
          <SupportAgent />
          <Trans>Поддержка в Телеграме</Trans>
        </StyledMenuItem>
        {!isHideMaterials && (
          <StyledMenuItem onClick={onClickMaterials}>
            <School />
            <Trans>Обучающие материалы</Trans>
          </StyledMenuItem>
        )}
      </StyledMenu>

      <BookDemoModal isOpen={isBookDemoOpen} onClose={handleCloseBookDemo} />
    </HelpContainer>
  );
};

export {HelpFab};
